import { EditorContent } from '@tiptap/react'
import styles from "../css/NoteEditor.module.css"
import { FaBold, FaCode, FaHeading, FaItalic, FaLaptopCode, FaListOl, FaListUl, FaStrikethrough, FaUnderline } from 'react-icons/fa'
import { useCallback, useEffect, useState } from 'react'

export default function NoteEditor({editor,editing = false}) {

    useEffect(() => {
        if (!editor) return
        const handleUpdate = () => {
            if (editor.isEmpty) {
                editor.commands.focus('start')
            }
        }
        editor.on('update', handleUpdate)
        return () => editor.off('update', handleUpdate)
    }, [editor])

    return (
        <div className = {`${styles.editor} ${editing ? styles.edit : ""} ${editor.isEmpty ? styles.empty : ""} ${(editor.isActive('bulletList') || editor.isActive('orderedList')) ? styles.activeList : ""}`}>
            {editor && <MenuBar editor={editor}/>}
            <div className = {styles.editorWrapper}>
                <EditorContent editor={editor} className={`${styles.textArea} editor`} />
            </div>
        </div>
    )
}

function MenuBar({ editor } ) {
  const [selectionVersion, setSelectionVersion] = useState(0)

  useEffect(() => {
    const handler = () => setSelectionVersion(v => v + 1)
    editor.on('selectionUpdate', handler)
    editor.on('transaction', handler)

    return () => {
      editor.off('selectionUpdate', handler)
      editor.off('transaction', handler)
    }
  }, [editor])

    const execute = (type) => {
        const selection = editor.state?.selection;
        const empty = selection ? selection.empty : true
        switch (type) {
            case 'bold':
                if (empty) {
                    editor.isActive('bold')
                    ? editor.chain().focus().unsetMark('bold').run()
                    : editor.chain().focus().setMark('bold').run();
                } else {
                    editor.chain().focus().toggleBold().run();
                }
                break;

            case 'italic':
                if (empty) {
                    editor.isActive('italic')
                    ? editor.chain().focus().unsetMark('italic').run()
                    : editor.chain().focus().setMark('italic').run();
                } else {
                    editor.chain().focus().toggleItalic().run();
                }
                break;

            case 'strike':
                if (empty) {
                    editor.isActive('strike')
                    ? editor.chain().focus().unsetMark('strike').run()
                    : editor.chain().focus().setMark('strike').run();
                } else {
                    editor.chain().focus().toggleStrike().run();
                }
                break;

            case 'underline':
                if (empty) {
                    editor.isActive('underline')
                    ? editor.chain().focus().unsetMark('underline').run()
                    : editor.chain().focus().setMark('underline').run();
                } else {
                    editor.chain().focus().toggleUnderline().run();
                }
                break;

            case 'heading':
                if (empty) {
                    editor.isActive('heading', { level: 2 })
                    ? editor.chain().focus().setParagraph().run()
                    : editor.chain().focus().setNode('heading', { level: 2 }).run();
                } else {
                    editor.chain().focus().toggleHeading({ level: 2 }).run();
                }
                break;

            case 'bulletList':
                editor.chain().focus().toggleBulletList().run();
                break;

            case 'orderedList':
                editor.chain().focus().toggleOrderedList().run();
                break;
            case 'codeBlock': 
                if (editor.isActive('codeBlock')) {
                    editor.chain().focus().toggleCodeBlock().run()
                    
                    setTimeout(() => {
                        const { from } = editor.state.selection
                        const $pos = editor.state.doc.resolve(from)
                        const parent = $pos.parent
                        
                        if (parent.type.name === 'paragraph' && parent.textContent === '\u200B') {
                            const start = $pos.start()
                            const end = $pos.end()
                            editor.chain()
                                .focus()
                                .deleteRange({ from: start, to: end })
                                .run()
                        }
                    }, 0)
                }
                else {
                    const { state } = editor
                    const { from, to } = state.selection
                    const selectedText = state.doc.textBetween(from, to, '\n')
                    editor.chain().focus().insertContent({
                        type: 'codeBlock',
                        attrs: { class: 'ProsemirrorEditor-codeBlock' },
                        content: [
                            {
                                type: 'text',
                                text: selectedText || '\u200B'
                            },
                        ],
                    }).run()
                }
                break;
            case 'code':
                if (editor.isActive('code')) {
                    editor.chain().focus().unsetMark('code').run();
                }
                else {
                    editor.chain().focus().setMark('code').run();
                }
                break;
            default:
        }
    }
    
    const isMarkActive = useCallback((mark) => {
        if (!editor) return false
        const { selection } = editor.state
        const { from, to } = selection
        const nodeTypes = ['heading', 'bulletList', 'orderedList', 'codeBlock']

        if (nodeTypes.includes(mark)) {
            if (mark === 'heading') {
                return [1, 2, 3].some(level => editor.isActive('heading', { level }))
            }
            return editor.isActive(mark)
        }
        if (selection.empty) {
            return editor.isActive(mark)
        }
        let hasTextNode = false
        let allHaveMark = true
        let onlyHardBreak = true
        editor.state.doc.nodesBetween(from, to, (node) => {
            if (node.type.name !== 'hardBreak') {
                onlyHardBreak = false
            }
            if (node.isText) {
                hasTextNode = true
                const markType = editor.schema.marks[mark]
                if (markType && !markType.isInSet(node.marks)) {
                    allHaveMark = false
                }
            }
        })
        if (onlyHardBreak || !hasTextNode) return false
        return allHaveMark
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, selectionVersion]
    )


    return (
        <div className = {styles.toolBar}>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('bold') ? styles.active : ''}  type ="button" disabled = {isMarkActive('codeBlock') || isMarkActive('code')} onClick={() => execute('bold')}><FaBold /></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('italic') ? styles.active : ''}  type ="button" disabled = {isMarkActive('codeBlock') || isMarkActive('code')} onClick={() => execute('italic')}><FaItalic /></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('underline') ? styles.active : ''} type ="button" disabled = {isMarkActive('codeBlock') || isMarkActive('code')} onClick={() => execute('underline')}><FaUnderline /></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('heading') ? styles.active : ''} type ="button" onClick={() => execute("heading")}><FaHeading /></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('bulletList') ? styles.active : ''} type ="button" onClick={() => execute("bulletList")}><FaListUl /></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('orderedList') ? styles.active : ''} type ="button" onClick={() => execute("orderedList")}><FaListOl /></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('strike') ? styles.active : ''} type ="button" disabled = {isMarkActive('codeBlock') || isMarkActive('code')} onClick={() => execute("strike")}><FaStrikethrough /></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('code') ? styles.active : ''} type ="button" onClick={() => execute("code")}><FaCode/></button>
            </div>
            <div className = {styles.buttonWrapper}>
                <button className = {isMarkActive('codeBlock') ? styles.active : ''} type ="button" onClick={() => execute("codeBlock")}><FaLaptopCode/></button>
            </div>
        </div>
  )
}
